import LockBodyDummy from '@/Components/LockBodyDummy';
import { CONTACT_DETAILS, SOCIAL_LINKS } from "@/Lib/siteData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "@/Components/Providers/AppProvider";
import { useForm, Form } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import Button from '@/Components/Button';
import { useEventListener, useMediaQuery } from 'usehooks-ts';

export enum OpenStyle { SHUTTERS,
    SLIDE_RIGHT,
    SLIDE_TOP,
    SLIDE_BOTTOM,
}

type ContactMeForm = {
    email: string,
    name: string,
    message: string,
};

type Props = {
    className?: string,
    openStyle?: OpenStyle,
};

export default function ContactForm({ className = "" }: Props) {
    const isDesktop = useMediaQuery('(min-width: 768px)');

    const {
        navigationBarAnchor,
        miniContactMenu: [open, setOpen],
    } = useAppContext();

    const [topPosition, setTopPosition] = useState(0);
    const { register, control, watch, reset, formState: { errors, isSubmitting }, } = useForm<ContactMeForm>();
    const [helpMessage, setHelpMessage] = useState<string | null>(null);
    const emailAddress = watch("email");

    const slideTransitionRules = (): Record<string, boolean> => {
        return {
            'translate-y-0': open,
            '-translate-y-full': !open,
        }
    }

    const handleContactClick = () => {
        setOpen(!open);
    };

    const handleSuccess = () => {
        setHelpMessage("We have received your message, and will contact you soon.");
        reset();
    };

    useEffect(() => {
        if (errors.email && emailAddress === "") {
            setHelpMessage("Please provide an email address");
        } else if (errors.email && emailAddress !== "") {
            setHelpMessage("Email address is not valid");
        } else {
            setHelpMessage(null);
        }
    }, [errors.email]);

    useEffect(() => {
        if (navigationBarAnchor?.current) {
            const height = navigationBarAnchor.current.offsetHeight;
            setTopPosition(height);
        }
    }, []);

    useEventListener('resize', () => {
        if (navigationBarAnchor?.current) {
            const height = navigationBarAnchor.current.offsetHeight;
            setTopPosition(height);
        }
    });

    return <div
        className={mc(className, { 'fixed bg-brand w-dvw px-8 py-6 lg:py-10 overflow-y-scroll transition-transform duration-150 ease-in-out z-40': true, ...slideTransitionRules() })}
        style={{
            top: `calc(${topPosition}px + ${isDesktop ? '2 * var(--spacing)' : '0px'})`,
            height: `calc(100dvh - ${topPosition}px - ${isDesktop ? '2 * var(--spacing)' : '0px'})`,
        }}
    >
        {open && <LockBodyDummy />}
        <div className={mc("relative z-10 flex flex-col justify-start items-center gap-6 h-full")}>
            <div className="w-full max-w-[64rem] h-full text-md lg:text-xl text-light uppercase text-medium gap-6 flex flex-col justify-between items-center z-10">
                <div className="w-full flex flex-row justify-between items-center">
                    <h2 className="font-bold space-x-2 md:space-x-4">
                        <span>Shoot us a Message.</span>
                    </h2>
                    <i className="text-[0.5rem] sm:text-xs font-sans normal-case">{helpMessage}</i>
                </div>
                <Form onSuccess={handleSuccess} action={route('public.contactForm')} headers={{ 'Content-Type': 'application/json' }} control={control} className="flex w-full h-full min-h-[20rem] md:min-h-[28rem] flex-col justify-between gap-4 text-sm md:text-md mb-4">
                    <div className="border-2 border-brand-light rounded-lg h-full">
                        <div className="flex justify-between h-10 md:h-16 border-b-2 border-brand-light">
                            <input type="text" placeholder="Name" className="w-1/2 p-4 border-r-2 border-brand-light appearance-none outline-none placeholder:text-font-light" {...register("name", { maxLength: 250 })}></input>
                            <input type="email" placeholder="Email" className="w-1/2 p-4 appearance-none outline-none placeholder:text-font-light" {...register("email", { required: true, maxLength: 100, pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/ })}></input>
                        </div>
                        <textarea placeholder="Say anything..." className="appearance-none outline-none resize-none text-sm p-4 w-full h-[calc(100%-10*var(--spacing))] md:h-[calc(100%-16*var(--spacing))] min-h-[16rem] placeholder:text-font-light" {...register("message", { maxLength: 250 })}></textarea>
                    </div>
                    <div className="flex flex-row justify-between gap-4">
                        <Button type="submit" disabled={isSubmitting} className={mc("border-2 border-brand-light p-2 md:p-4 rounded-lg grow-1", {
                                'cursor-not-allowed': isSubmitting,
                                'cursor-pointer [&:focus,&:hover]:bg-brand-light [&:focus,&:hover]:text-brand': !isSubmitting,
                            })}>
                            <div className="relative overflow-hidden">
                                {isSubmitting
                                    ? <FontAwesomeIcon icon={faCircleNotch} size="lg" className="animate-spin" />

                                    : <>
                                        <span className="block h-min group-[&:focus,&:hover]:-translate-y-full transition-transform ease-in-out duration-250">Send</span>
                                        <span className="absolute top-0 left-0 h-min text-brand translate-y-full group-[&:focus,&:hover]:translate-none transition-transform ease-in-out duration-250">Send</span>
                                    </>
                                }
                            </div>
                        </Button>
                        <Button type="reset" onClick={handleContactClick} className="grow-1 border-2 border-brand-light p-2 md:p-4 rounded-lg [&:focus,&:hover]:bg-brand-light [&:focus,&:hover]:text-brand">Cancel</Button>
                    </div>
                </Form>
                <div className="w-full flex flex-row justify-end sm:justify-between items-start md:items-center py-4 uppercase text-xs md:text-sm md:mb-4">
                    <span className="hidden sm:block text-[0.6rem] md:text-sm max-w-32 md:max-w-none" dangerouslySetInnerHTML={{ __html: CONTACT_DETAILS.companyLegalName }}></span>
                    <div className="flex flex-col justify-end items-end md:flex-row md:justify-between md:items-center gap-4 md:gap-8">
                        <ul className="flex flex-row justify-between items-center gap-2 md:gap-4 text-xs text-[0.6rem] sm:text-sm lg:text-md">
                            <li><a href={`mailto:${CONTACT_DETAILS.email}`} className="[&:focus,&:hover]:text-brand-light outline-none appearance-none">{CONTACT_DETAILS.email}</a></li>
                            <li className="w-[2px] h-[12px] bg-font rounded"></li>
                            <li><a href={CONTACT_DETAILS.whatsapp} target="_blank" rel="noopener noreferrer" className="[&:focus,&:hover]:text-brand-light outline-none appearance-none">{CONTACT_DETAILS.cellphone}</a></li>
                        </ul>
                        <ul className="flex flex-row justify-end gap-4">
                            {SOCIAL_LINKS.filter(({ type }) => type !== 'email').map(({ href, handle, icon }) => <li key={`${href}_${handle}`} className="group cursor-pointer">
                                <a href={href} className="group appearance-none outline-none">
                                    <FontAwesomeIcon icon={icon} size={isDesktop ? "2xl" : "xl"} className="text-light group-hover:text-brand-light group-focus:text-brand-light transition-all ease-in-out" />
                                </a>
                            </li>)}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>

}
